import { useLayoutEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { useAuth } from '@/context/AuthContext';
import { getActiveSessionRevision, subscribeActiveSessionRevision } from '@/api/authenticatedClient';
import { API } from '@/config/constants';
import { resolveBackendUrl } from '@/config/desktopBootstrap';
import { getIntegrations, connectIntegration, setStripeWebhookSecret, disconnectIntegration,
  getStripeSendingFlorist, setStripeSendingFlorist } from '@/api/integrations';
import { getSendingFlorists } from '@/api/sources';

// Integration routes currently address the backend's default settings resource,
// irrespective of the caller's tenant. This gate is endpoint-wide, in-memory
// only, and retains no credentials, drafts, or replayable operation payloads.
const resources = new Map();
let sequence = 0;
const resourceFor = endpoint => {
  if (!resources.has(endpoint)) resources.set(endpoint, { pending: null, statusEpoch: 0, mappingEpoch: 0, observers: new Set() });
  return resources.get(endpoint);
};
const object = value => !!value && typeof value === 'object' && !Array.isArray(value);
const text = value => typeof value === 'string' && !!value;
const florist = value => object(value) && text(value.id) && text(value.name);
const validStatus = value => object(value) && ['stripe', 'google_maps'].every(key => value[key] === undefined || object(value[key]));
const validMapping = (rows, value) => Array.isArray(rows) && rows.every(florist)
  && new Set(rows.map(row => row.id)).size === rows.length && object(value)
  && (value.sending_florist_id === null || text(value.sending_florist_id))
  && (value.sending_florist == null || (florist(value.sending_florist) && value.sending_florist.id === value.sending_florist_id));
const refused = error => [400, 401, 403, 404, 409, 422].includes(error?.response?.status);
const connection = () => JSON.stringify([window.lvbDesktop?.backendUrl, window.lvbDesktop?.localBackendUrl]);
const transportMatches = () => {
  const backend = resolveBackendUrl(window.location.protocol, window.lvbDesktop, process.env.REACT_APP_BACKEND_URL);
  return API !== null && backend !== null && `${backend || ''}/api` === API;
};
const identity = binding => JSON.stringify([binding?.user?.id, binding?.user?.tenant_id, binding?.source]);

export function createIntegrationSettingsController(authorize, endpoint) {
  const resource = resourceFor(endpoint);
  const key = ++sequence;
  const listeners = new Set();
  const reads = new Set();
  let active = false;
  let life = 0;
  let statusVersion = 0;
  let mappingVersion = 0;
  let queued = false;
  let clearSecrets = null;
  let snapshot = { integrations: null, loading: true, statusError: false, florists: [], selectedFloristId: '',
    mappingLoading: true, mappingError: false, mappingKnown: false, pending: false, active: false, outcome: '' };
  const owns = () => active && !!authorize();
  const notify = () => {
    snapshot = { ...snapshot, pending: !!resource.pending, active: owns() };
    listeners.forEach(listener => listener());
  };
  const patch = update => { snapshot = { ...snapshot, ...update }; notify(); };
  const read = async (mapping, allowedOperation = null) => {
    if (!owns()) return false;
    if (resource.pending && resource.pending !== allowedOperation) {
      if (!mapping) queued = true;
      return false;
    }
    const config = authorize();
    const request = new AbortController();
    const started = life;
    const version = mapping ? ++mappingVersion : ++statusVersion;
    const epoch = mapping ? resource.mappingEpoch : resource.statusEpoch;
    const current = () => owns() && started === life && !request.signal.aborted
      && version === (mapping ? mappingVersion : statusVersion)
      && epoch === (mapping ? resource.mappingEpoch : resource.statusEpoch);
    reads.add(request);
    patch(mapping ? { mappingLoading: true, mappingKnown: false } : { loading: true });
    try {
      await Promise.resolve();
      if (!current()) return false;
      const captured = { ...config, signal: request.signal };
      if (mapping) {
        // GET mapping may repair a dangling reference; guard its own dispatch.
        const dispatch = execute => Promise.resolve().then(() => {
          if (!current()) throw new Error('Mapping observation retired');
          return execute(captured);
        });
        const [rows, value] = await Promise.all([dispatch(getSendingFlorists), dispatch(getStripeSendingFlorist)]);
        if (!current()) return false;
        if (!validMapping(rows, value)) throw new Error('Invalid mapping observation');
        const selected = value.sending_florist_id || '';
        const choices = rows.slice();
        if (selected && !choices.some(row => row.id === selected)) {
          choices.push(value.sending_florist || { id: selected, name: 'Current default (not in the available directory)' });
        }
        patch({ florists: choices, selectedFloristId: selected, mappingLoading: false, mappingError: false, mappingKnown: true });
      } else {
        const value = await getIntegrations(captured);
        if (!current()) return false;
        if (!validStatus(value)) throw new Error('Invalid configuration observation');
        patch({ integrations: value, loading: false, statusError: false });
      }
      return true;
    } catch {
      if (current()) patch(mapping ? { mappingLoading: false, mappingError: true, mappingKnown: false } : { loading: false, statusError: true });
      return false;
    } finally { reads.delete(request); }
  };
  const changed = (phase, operation, changesMapping) => {
    if (phase === 'begin') {
      ++statusVersion;
      if (changesMapping) ++mappingVersion;
      patch({ loading: false, statusError: true, ...(changesMapping ? { mappingLoading: false, mappingKnown: false } : {}) });
    } else {
      notify();
      if (owns() && (operation.owner !== key || operation.life !== life)) { read(false); read(true); }
    }
  };
  const publish = (phase, operation, changesMapping) => resource.observers.forEach(observer => observer(phase, operation, changesMapping));
  const mutate = async (kind, input) => {
    if (!owns() || resource.pending || (kind === 'mapping' && !snapshot.mappingKnown)) return { kind: 'blocked' };
    const config = authorize();
    if (!config) return { kind: 'retired' };
    const started = life;
    const current = () => owns() && started === life;
    const operation = { owner: key, life, phase: kind };
    const changesMapping = kind !== 'credentials';
    resource.pending = operation;
    ++resource.statusEpoch;
    if (changesMapping) ++resource.mappingEpoch;
    publish('begin', operation, changesMapping);
    patch({ outcome: '' });
    // Secrets stay local to this owner, never in the shared gate. Retirement
    // clears the undispatched second value while an existing request settles.
    let apiKey = kind === 'credentials' ? input.apiKey : '';
    let secret = kind === 'credentials' ? input.secret : '';
    input = kind === 'mapping' ? input : null;
    const erase = () => { apiKey = ''; secret = ''; };
    clearSecrets = erase;
    const request = new AbortController();
    const captured = { ...config, signal: request.signal };
    let apiSaved = false;
    let result = { kind: 'unknown' };
    let message = '';
    try {
      if (!current()) return { kind: 'retired' };
      if (kind === 'credentials') {
        const pending = connectIntegration('stripe', apiKey, captured);
        apiKey = '';
        const first = await pending;
        if (!current()) return { kind: 'retired' };
        if (first?.success !== true || first.integration !== 'stripe') throw new Error('Unconfirmed API-key save');
        apiSaved = true;
        const secondRequest = setStripeWebhookSecret(secret, captured);
        secret = '';
        const second = await secondRequest;
        if (!current()) return { kind: 'retired' };
        if (second?.success !== true || typeof second.masked_key !== 'string') throw new Error('Unconfirmed signing-secret save');
        message = 'Stripe API key and signing secret were saved.';
      } else if (kind === 'remove') {
        const value = await disconnectIntegration('stripe', captured);
        if (!current()) return { kind: 'retired' };
        if (value?.success !== true || value.integration !== 'stripe') throw new Error('Unconfirmed removal');
        message = 'Saved Stripe settings, including the sending-florist default, were removed. Server credentials were not changed.';
        patch({ selectedFloristId: '' });
      } else {
        const value = await setStripeSendingFlorist(input, captured);
        if (!current()) return { kind: 'retired' };
        if (value?.success !== true || value.sending_florist_id !== (input || null)) throw new Error('Unconfirmed mapping save');
        message = input ? 'Sending florist linked to Stripe.' : 'Stripe sending-florist cleared.';
        patch({ selectedFloristId: input || '' });
      }
      result = { kind: 'accepted' };
    } catch (error) {
      result = { kind: refused(error) ? 'rejected' : 'unknown' };
      if (kind === 'credentials') {
        message = result.kind === 'rejected'
          ? `${apiSaved ? 'The API key was saved. The signing-secret save' : 'The API-key save'} was refused. ${apiSaved ? '' : 'The signing-secret save was not attempted. '}Review access and entered values before making a deliberate correction.`
          : apiSaved
            ? 'The API key was saved. The signing-secret save was not confirmed and may have taken effect. Refresh and re-enter credentials deliberately to correct them; nothing will be resent automatically.'
            : 'The API-key save was not confirmed and may have taken effect. The signing-secret save was not attempted. Refresh and re-enter credentials deliberately to correct them; nothing will be resent automatically.';
      } else if (kind === 'remove') {
        message = result.kind === 'rejected' ? 'Removal was refused. Review access before another deliberate action.'
          : 'Removal was not confirmed and may have taken effect. Refresh before deciding whether to try again.';
      } else {
        message = result.kind === 'rejected' ? 'The sending-florist change was refused. Review the current default before another action.'
          : 'The sending-florist change was not confirmed and may have taken effect. Refresh and review the current default before another deliberate change.';
      }
    } finally {
      erase();
      if (clearSecrets === erase) clearSecrets = null;
      if (current()) {
        patch({ outcome: message });
        await Promise.all([read(false, operation), ...(changesMapping ? [read(true, operation)] : [])]);
      }
      if (resource.pending === operation) {
        resource.pending = null;
        publish('end', operation, changesMapping);
      }
      if (current() && queued) { queued = false; await read(false); }
    }
    return current() ? result : { kind: 'retired' };
  };
  return {
    key, owns, mutate, refresh: () => read(false), refreshMapping: () => read(true),
    subscribe: listener => { listeners.add(listener); return () => listeners.delete(listener); }, getSnapshot: () => snapshot,
    activate() {
      active = true; ++life; resource.observers.add(changed); notify();
      if (resource.pending) patch({ loading: false, statusError: true, mappingLoading: false, mappingKnown: false });
      else if (owns()) { read(false); read(true); }
      else patch({ loading: false, statusError: true, mappingLoading: false, mappingError: true });
    },
    retire() { active = false; ++life; ++statusVersion; ++mappingVersion; queued = false; clearSecrets?.(); reads.forEach(request => request.abort()); resource.observers.delete(changed); notify(); },
  };
}

export function useIntegrationSettings() {
  const { sessionBinding, getToken } = useAuth() || {};
  const revision = useSyncExternalStore(subscribeActiveSessionRevision, getActiveSessionRevision);
  const principal = identity(sessionBinding);
  const source = connection();
  const roles = sessionBinding?.user?.roles || [sessionBinding?.user?.role];
  const permitted = roles.includes('admin');
  const transportCurrent = transportMatches();
  const latest = useRef(null);
  latest.current = { sessionBinding, getToken, source };
  const controller = useMemo(() => {
    const authorize = () => {
      const current = latest.current;
      const binding = current.sessionBinding;
      const currentRoles = binding?.user?.roles || [binding?.user?.role];
      if (!permitted || !binding?.user?.id || identity(binding) !== principal || binding.revision !== revision
        || getActiveSessionRevision() !== revision || !currentRoles.includes('admin')
        || current.source !== source || connection() !== source || !transportMatches()) return null;
      const token = current.getToken?.();
      return token ? { headers: { Authorization: `Bearer ${token}` }, timeout: 30000, suppressServerErrorNotification: true } : null;
    };
    return createIntegrationSettingsController(authorize, API);
  }, [principal, revision, source, permitted]);
  useLayoutEffect(() => { controller.activate(); return () => controller.retire(); }, [controller]);
  const snapshot = useSyncExternalStore(controller.subscribe, controller.getSnapshot);
  return { controller, ...snapshot, connectionError: !transportCurrent };
}
